import { useState } from "react";
import { EyeOff, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminStatusBadge } from "@/admin/components/AdminStatusBadge";
import { ReasonModal } from "@/admin/components/ReasonModal";
import { useAdsModeration } from "@/admin/hooks/useAdsModeration";
import type { ModerationStatus, AdminAnnonceRow } from "@/admin/types/admin";
import { MODERATION_STATUS_LABELS } from "@/admin/types/admin";

const FILTERS: ModerationStatus[] = ["active", "underReview", "hidden", "removed"];
const FILTER_ALL = "all";

export default function AdminAdsModeration() {
  const [filter, setFilter] = useState<ModerationStatus | typeof FILTER_ALL>(FILTER_ALL);
  const [hideTarget, setHideTarget] = useState<AdminAnnonceRow | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminAnnonceRow | null>(null);

  const effectiveFilter = filter === FILTER_ALL ? undefined : filter;
  const { query, hideMutation, removeMutation, markSafeMutation } = useAdsModeration(effectiveFilter);

  const ads = query.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Modération des annonces</h1>
        <p className="text-sm text-gray-500 mt-1">{ads.length} annonce(s)</p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(FILTER_ALL)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
            filter === FILTER_ALL
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Toutes
        </button>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === f
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {MODERATION_STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {/* Table */}
      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : ads.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-400">Aucune annonce trouvée</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Titre</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Auteur</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Wilaya</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Statut modération</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ads.map((ad) => (
                <tr key={ad.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 max-w-[200px] truncate">{ad.title}</p>
                    <p className="text-xs text-gray-500">{ad.metier}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{ad.authorName}</td>
                  <td className="px-4 py-3 text-gray-700">{ad.wilaya}</td>
                  <td className="px-4 py-3">
                    <AdminStatusBadge status={ad.moderationStatus} />
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(ad.createdAt).toLocaleDateString("fr-FR")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {(ad.moderationStatus === "active" || ad.moderationStatus === "underReview") && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50"
                            onClick={() => setHideTarget(ad)}
                          >
                            <EyeOff className="h-3.5 w-3.5" />
                            Masquer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-red-700 border-red-300 hover:bg-red-50"
                            onClick={() => setRemoveTarget(ad)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Supprimer
                          </Button>
                        </>
                      )}
                      {(ad.moderationStatus === "hidden" || ad.moderationStatus === "underReview") && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                          disabled={markSafeMutation.isPending}
                          onClick={() => markSafeMutation.mutate(ad.id)}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          Conforme
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal masquage */}
      <ReasonModal
        open={!!hideTarget}
        title={`Masquer l'annonce "${hideTarget?.title ?? ""}"`}
        reasonCategory="ad_moderation"
        loading={hideMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!hideTarget) return;
          hideMutation.mutate(
            { annonceId: hideTarget.id, reasonCode, notes },
            { onSuccess: () => setHideTarget(null) }
          );
        }}
        onCancel={() => setHideTarget(null)}
      />

      {/* Modal suppression */}
      <ReasonModal
        open={!!removeTarget}
        title={`Supprimer l'annonce "${removeTarget?.title ?? ""}"`}
        reasonCategory="ad_moderation"
        loading={removeMutation.isPending}
        onConfirm={(reasonCode, notes) => {
          if (!removeTarget) return;
          removeMutation.mutate(
            { annonceId: removeTarget.id, reasonCode, notes },
            { onSuccess: () => setRemoveTarget(null) }
          );
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
